import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import api from '../api/api';
import ActivityIndicatorScreen from '../components/ActivityIndicatorScreen';
import Colors from '../constants/Colors';
import { useGetCurrentUser } from '../hooks/user/useGetCurrentUser';
import { useGetUserStudyParticipants } from '../hooks/user/useGetUserStudyParticipants';
import { User } from '../models/User';
import {
  filterOutRemovedOccurrences,
  getAllEventOccurrences,
  minEventOccurrence,
  ParticipantEventOccurrence,
  sortEventOccurrences
} from '../screens/study/helpers/ParticipantEventHelpers';
import * as Notifications from 'expo-notifications';
import { StudyParticipant } from '../models/StudyParticipant';
import { crashlyticsSetAttribute } from '../common/crashlytics';
import { clearSentryUser, setSentryUser } from '../common/sentry';
import { appcuesIdentify } from '../common/appcues';
import { Platform, Settings } from 'react-native';
import * as Device from 'expo-device';
import { gActiveStudyParticipant } from '../hooks/study/useHasActiveStudy';
import { getFitbitTokenExpiration } from '../tools/FitbitHelper';
import { differenceInCalendarDays } from 'date-fns';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { clearOtherUsersFormDrafts } from '../dynamic-forms/drafts/formDraftStore';

export type UserContextProps = ReturnType<
  typeof useGetUserStudyParticipants
> & {
  loadingUser?: boolean;
  accessToken: string | null;
  user?: User | null;
  clearUserSession: () => void;
  setUser: (user: User) => void;
  updateUserSession: () => Promise<void>;
  setAccessToken: (accessToken: string) => void;
  studyParticipants?: StudyParticipant[];
  futureOccurrences: ParticipantEventOccurrence[];
  pastOccurrences: ParticipantEventOccurrence[];
  completedOccurrences: ParticipantEventOccurrence[];
  expiredOccurrences: ParticipantEventOccurrence[];
  nextOccurrence: ParticipantEventOccurrence | undefined;
  allOccurrences: ParticipantEventOccurrence[];
  persistentOccurrences: ParticipantEventOccurrence[];
};

interface GetUserContextParams {
  ignoreUndefined?: boolean;
}

export const UserContext = createContext<UserContextProps>(null!);

export const useUserContext = (params?: GetUserContextParams) => {
  const context = useContext(UserContext);
  if (!context && !params?.ignoreUndefined) {
    throw new Error(
      'useUserContext must be used within the context of the UserProvider component'
    );
  }
  return context;
};

export interface UserProviderProps {
  /**
   * Used by the web embed only
   */
  userId?: number;
}

export const UserProvider: React.FC<PropsWithChildren & UserProviderProps> = ({
  children,
  userId
}) => {
  const { loadingUser, userError, getUser } = useGetCurrentUser();
  const [state, setState] = useState<{
    user?: User | null;
    accessToken?: string;
    studyParticipants?: StudyParticipant[];
  }>({});

  // Get Study Participant data after user loads
  const {
    loadingStudyParticipants,
    loadingStudyParticipantsError,
    getStudyParticipants
  } = useGetUserStudyParticipants();

  const clearUserSession = useCallback(() => {
    setState({ user: null });
  }, []);

  const setUser = useCallback((user: User) => {
    setState(state => ({ ...state, user }));
  }, []);

  const setAccessToken = useCallback((accessToken: string) => {
    setState(state => ({ ...state, accessToken }));
  }, []);

  const updateUserSession = useCallback(async () => {
    try {
      const accessToken = await api.getAccessToken();
      if (accessToken) {
        const user = (await getUser(userId)) ?? null;
        const studyParticipants = user?.id
          ? await getStudyParticipants(user.id)
          : undefined;

        // Ensure that access token still exists before setting state,
        // this is a workaround to prevent setting state data
        // when user navigates to My Profile screen and updateUserSession was called on useFocusEffect
        // and the user already logs out before the response from getUser arrives
        // causing the issue where state still has values.
        const accessToken = await api.getAccessToken();
        if (accessToken) {
          setState({ user, accessToken, studyParticipants });
        }
      } else {
        clearUserSession();
      }
    } catch (error) {
      // A transport-level failure (e.g. cold start before connectivity is
      // ready) must not become an unhandled rejection: updateUserSession is
      // invoked fire-and-forget from the bootstrap effect and many screen
      // focus effects. The error already surfaces via userError for the UI,
      // and the underlying GET self-heals via the retry in useHttpFetch.
      // eslint-disable-next-line no-console
      console.warn('[UserContext] updateUserSession failed', error);
    }
  }, [userId, getUser, getStudyParticipants, clearUserSession]);

  useEffect(() => {
    (async () => {
      if (Settings.get('clear_cache')) {
        await AsyncStorage.clear();
        Settings.set({ clear_cache: 0 });
      }
    })();
  }, []);

  useEffect(() => {
    if (userError?.statusCode === 403) api.removeAccessToken();
  }, [userError]);

  useEffect(() => {
    if (state.user?.id) clearOtherUsersFormDrafts(state.user.id);
  }, [state.user?.id]);

  useEffect(() => {
    // add user id key and value to crash report
    (async () => {
      if (state.user) {
        await crashlyticsSetAttribute('user id', state.user.id.toString());
        setSentryUser({ id: state.user.id });
      } else if (state.user === null) {
        clearSentryUser();
      }
    })();
  }, [state.user]);

  // Get participant event occurences
  const {
    futureOccurrences,
    pastOccurrences,
    completedOccurrences,
    expiredOccurrences,
    nextOccurrence,
    allOccurrences,
    persistentOccurrences
  } = useMemo(() => {
    // console.log('studyParticipants', studyParticipants);
    let futureOccurrences: ParticipantEventOccurrence[] = [];
    let pastOccurrences: ParticipantEventOccurrence[] = [];
    let completedOccurrences: ParticipantEventOccurrence[] = [];
    let expiredOccurrences: ParticipantEventOccurrence[] = [];
    let nextOccurrence: ParticipantEventOccurrence | undefined;
    let allOccurrences: ParticipantEventOccurrence[] = [];
    let persistentOccurrences: ParticipantEventOccurrence[] = [];
    for (const studyParticipant of state?.studyParticipants || []) {
      if (!studyParticipant.paused) {
        const participantEvents = studyParticipant.participantEvents;
        for (const participantEvent of participantEvents || []) {
          const { next, future, past, completed, expired, all, persistent } =
            getAllEventOccurrences(participantEvent, studyParticipant);
          // console.log(
          //   'next',
          //   next?.intendedStartDate,
          //   next?.participantEvent?.name
          // );
          // prioritize the most immediate next occurrence
          nextOccurrence = minEventOccurrence(nextOccurrence, next);
          futureOccurrences.push(...future);
          pastOccurrences.push(...past);
          completedOccurrences.push(...completed);
          expiredOccurrences.push(...expired);
          allOccurrences.push(...all);
          persistentOccurrences.push(...persistent);
        }
      }
    }
    futureOccurrences = filterOutRemovedOccurrences(
      sortEventOccurrences(futureOccurrences, 'DESC')
    );
    pastOccurrences = filterOutRemovedOccurrences(
      sortEventOccurrences(pastOccurrences, 'DESC')
    );
    completedOccurrences = filterOutRemovedOccurrences(
      sortEventOccurrences(completedOccurrences, 'DESC')
    );
    expiredOccurrences = filterOutRemovedOccurrences(
      sortEventOccurrences(expiredOccurrences, 'DESC')
    );
    allOccurrences = filterOutRemovedOccurrences(
      sortEventOccurrences(allOccurrences, 'DESC')
    );
    persistentOccurrences = filterOutRemovedOccurrences(
      sortEventOccurrences(persistentOccurrences, 'DESC')
    );
    // console.log('future', futureOccurrences);
    return {
      futureOccurrences,
      pastOccurrences,
      completedOccurrences,
      expiredOccurrences,
      nextOccurrence,
      allOccurrences,
      persistentOccurrences
    };
  }, [state]);

  // when UserContext first loads get user session if access token exists
  useEffect(() => {
    updateUserSession();
  }, [updateUserSession]);
  const allOccurrencesRef = useRef(allOccurrences);
  allOccurrencesRef.current = allOccurrences;
  useEffect(() => {
    (async () => {
      // identify user appcues
      if (state.user) {
        let daysUntilWearableTokenExpiration;
        const studyParticipant = gActiveStudyParticipant(
          state.studyParticipants ?? [],
          allOccurrencesRef.current
        );
        if (state.user.fitbitAccessToken) {
          const fitbitExpirationDate = await getFitbitTokenExpiration();
          if (fitbitExpirationDate) {
            daysUntilWearableTokenExpiration = differenceInCalendarDays(
              parseInt(fitbitExpirationDate, 10),
              new Date()
            );
          }
        }
        appcuesIdentify(String(state.user.id), {
          isStudyInProgress: studyParticipant ? String(true) : String(false),
          studyId: studyParticipant?.studyId ?? undefined,
          wearableType: state.user.fitbitAccessToken
            ? 'Fitbit'
            : state.user.garminConfig
              ? 'Garmin'
              : undefined,
          osName: Platform.OS,
          osVersion: Platform.Version,
          deviceManufacturer: Device.brand,
          deviceModelName: Device.deviceName,
          daysUntilWearableTokenExpiration
        });
      }
    })();
  }, [state.studyParticipants, state.user]);

  // set badge icon for number of due now events
  Notifications.setBadgeCountAsync(pastOccurrences.length);

  if (typeof state.user === 'undefined') {
    return <ActivityIndicatorScreen color={Colors.primary} />;
  }

  return (
    <UserContext.Provider
      value={{
        accessToken: state?.accessToken ?? null,
        loadingUser: loadingUser || loadingStudyParticipants,
        user: state?.user,
        setUser,
        clearUserSession,
        updateUserSession,
        setAccessToken,
        studyParticipants: state.studyParticipants,
        loadingStudyParticipants,
        loadingStudyParticipantsError: loadingStudyParticipantsError,
        getStudyParticipants,
        futureOccurrences,
        pastOccurrences,
        completedOccurrences,
        expiredOccurrences,
        nextOccurrence,
        allOccurrences,
        persistentOccurrences
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
